import React from 'react';
import { Box } from '@indeed/ifl-components';
import { DocReview, Jobs, Profile } from '@indeed/ifl-icons';
import { AutoApplyHeader } from '../../../components/AutoApplyHeader/AutoApplyHeader';
import GettextFactory from '../../../util/GettextFactory';
import { MatchPreferencesSection } from '../components/MatchPreferencesSection';
import { ContactInformationSection } from './ContactInformationSection';
import {
    ResumePreviewSection,
    type ReviewIndeedResume,
    type ReviewSelectedFileResume,
} from './ResumePreviewSection';
import { ApplicationQuestionsSection } from './ApplicationQuestionsSection';
import type { ContactInformationData } from './contactInfo';

export interface CustomizeExperienceReviewProps {
    contactInfo: ContactInformationData;
    showIndeedResume: boolean;
    showFileResume: boolean;
    indeedResume: ReviewIndeedResume | undefined;
    selectedFileResume: ReviewSelectedFileResume | null;
    pdfUrl: string | null;
    loadingPdf: boolean;
    locale: string;
    onEditResume: () => void;
    /** Whether the SQ (screening question) prefill toggle is on. Default ON. */
    sqOptedIn: boolean;
    /** Saves the SQ preference immediately through updateAutoApply. */
    onSqOptInChange: (checked: boolean) => void;
    /** Whether the DQ (voluntary self-identification) prefill toggle is on. Default OFF. */
    dqOptedIn: boolean;
    /** Saves DQ consent immediately through setDqPrefillConsent. */
    onDqOptInChange: (checked: boolean) => void;
    /** Whether the DQ experiment exposes its preference control. */
    showDqToggle: boolean;
    /** Prevent preference changes and edit navigation while saving or leaving the page. */
    preferencesDisabled?: boolean;
}

/**
 * "Customize your experience" Review variant, shown when the
 * `rnhp_auto_apply_banner_tst` proctor is in the progressive-automation group
 * (value === 2). Renders the manage/edit content: Job preferences, Contact
 * information, Resume, and Application questions.
 *
 * Unlike the legacy Review page, every section is shown unconditionally (the
 * per-section proctors are bypassed in this variant). The SQ/DQ preference toggles
 * save immediately. ReviewPage persists SQ through
 * `updateAutoApply` and DQ through the account-level `setDqPrefillConsent` mutation.
 *
 * Figma: https://www.figma.com/design/YqvUoqcp7hUVEjilAGM0ls/Auto-Apply-2026?node-id=19431-64136
 * Figma (section icons/subheader): https://www.figma.com/design/YqvUoqcp7hUVEjilAGM0ls/Auto-Apply-2026?node-id=23102-64891
 * JIRA: IAFE-2080, IAFE-3428
 *
 * Rendered inside ReviewPage's PageTransition/Helmet wrapper. Preferences save
 * on edit; only activation requires a separate primary action.
 */
export const CustomizeExperienceReview: React.FC<CustomizeExperienceReviewProps> = ({
    contactInfo,
    showIndeedResume,
    showFileResume,
    indeedResume,
    selectedFileResume,
    pdfUrl,
    loadingPdf,
    locale,
    onEditResume,
    sqOptedIn,
    onSqOptInChange,
    dqOptedIn,
    onDqOptInChange,
    showDqToggle,
    preferencesDisabled,
}) => {
    const gt = GettextFactory();

    // In this variant the Contact section is shown whenever contact data exists,
    // independent of the per-section `auto_apply_setup_review_contact_info` proctor.
    const hasContactData = !!(
        contactInfo.fullName ||
        contactInfo.phoneNumber ||
        contactInfo.country ||
        contactInfo.cityState ||
        contactInfo.address ||
        contactInfo.emailAddress
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <AutoApplyHeader
                title={gt.gettext('Customize your experience')}
                description={gt.gettext(
                    'Update your job match preferences and choose how applications are autofilled.',
                )}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {/* Job preferences */}
                <MatchPreferencesSection
                    title={gt.gettext('Job preferences')}
                    icon={<Jobs size="md" aria-hidden />}
                    editDisabled={preferencesDisabled}
                />

                {/* Contact information */}
                {hasContactData && (
                    <ContactInformationSection
                        contactInfo={contactInfo}
                        icon={<Profile size="md" aria-hidden />}
                        editDisabled={preferencesDisabled}
                    />
                )}

                {/* Resume */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <ResumePreviewSection
                        heading={gt.gettext('Resume')}
                        icon={<DocReview size="md" aria-hidden />}
                        onEditResume={onEditResume}
                        editDisabled={preferencesDisabled}
                        showIndeedResume={showIndeedResume}
                        showFileResume={showFileResume}
                        indeedResume={indeedResume}
                        selectedFileResume={selectedFileResume}
                        pdfUrl={pdfUrl}
                        loadingPdf={loadingPdf}
                        locale={locale}
                    />
                </Box>

                {/* Application questions */}
                <ApplicationQuestionsSection
                    sqOptedIn={sqOptedIn}
                    onSqOptInChange={onSqOptInChange}
                    dqOptedIn={dqOptedIn}
                    onDqOptInChange={onDqOptInChange}
                    showDqToggle={showDqToggle}
                    preferencesDisabled={preferencesDisabled}
                />
            </Box>
        </Box>
    );
};
